import { useState, useEffect } from 'react';
import { getMortgage, logMortgagePayment } from '../api';
import { styles } from '../styles';
import { Spinner } from '../components/Icons';

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M19 12H5M12 5l-7 7 7 7" />
    </svg>
  );
}

export default function MortgagePaymentScreen({ currentUser, onBack, onSave }) {
  const [mortgageData, setMortgageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const today = new Date().toISOString().split('T')[0];
  const [paymentDate, setPaymentDate] = useState(today);
  const [totalAmount, setTotalAmount] = useState('');
  const [customInterest, setCustomInterest] = useState('');
  const [overrideInterest, setOverrideInterest] = useState(false);
  const [createReceipt, setCreateReceipt] = useState(true);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    getMortgage()
      .then(d => {
        setMortgageData(d);
        // Pre-fill total amount with scheduled payment
        setTotalAmount(String(Number(d.next_scheduled_payment).toFixed(2)));
        setLoading(false);
      })
      .catch(() => {
        setError('No mortgage configured');
        setLoading(false);
      });
  }, []);

  // Compute expected interest from current balance
  const currentBalance = mortgageData ? Number(mortgageData.current_balance) : 0;
  const annualRate = mortgageData ? Number(mortgageData.mortgage.annual_rate) : 0;
  const expectedInterest = currentBalance * (annualRate / 12);

  const total = parseFloat(totalAmount) || 0;
  const interestUsed = overrideInterest
    ? (parseFloat(customInterest) || 0)
    : expectedInterest;
  const principalPaid = Math.max(0, total - interestUsed);
  const balanceAfter = Math.max(0, currentBalance - principalPaid);
  const scheduledPayment = mortgageData ? Number(mortgageData.next_scheduled_payment) : 0;
  const extraPrincipal = Math.max(0, total - scheduledPayment);

  const handleSave = async () => {
    setError(null);
    if (!total || total <= 0) {
      setError('Enter a payment amount');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        payment_date: paymentDate,
        total_payment: total,
        interest_paid: overrideInterest ? interestUsed : null,
        notes: notes || null,
        create_receipt: createReceipt,
      };
      await logMortgagePayment(payload, currentUser);
      onSave();
    } catch (e) {
      setError(e.message || 'Failed to log payment');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ ...styles.screen, ...styles.centered }}>
        <Spinner />
      </div>
    );
  }

  return (
    <div style={styles.screen}>
      <header style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}><BackIcon /></button>
        <h1 style={styles.headerTitle}>Log Payment</h1>
        <div style={{ width: 40 }} />
      </header>

      <div style={pmtStyles.formCard}>
        <label style={pmtStyles.label}>Payment Date</label>
        <input
          style={pmtStyles.input}
          type="date"
          value={paymentDate}
          onChange={e => setPaymentDate(e.target.value)}
        />

        <label style={pmtStyles.label}>Total Amount Paid ($)</label>
        <input
          style={pmtStyles.input}
          type="number"
          inputMode="decimal"
          step="0.01"
          value={totalAmount}
          onChange={e => setTotalAmount(e.target.value)}
          placeholder={scheduledPayment.toFixed(2)}
        />

        {/* Interest override */}
        <div style={pmtStyles.checkRow}>
          <input
            type="checkbox"
            id="overrideInterest"
            checked={overrideInterest}
            onChange={e => setOverrideInterest(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          <label htmlFor="overrideInterest" style={pmtStyles.checkLabel}>
            Override interest amount
          </label>
        </div>
        {overrideInterest && (
          <input
            style={pmtStyles.input}
            type="number"
            inputMode="decimal"
            step="0.01"
            value={customInterest}
            onChange={e => setCustomInterest(e.target.value)}
            placeholder={expectedInterest.toFixed(2)}
          />
        )}

        <label style={pmtStyles.label}>Notes (optional)</label>
        <input
          style={pmtStyles.input}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="e.g. Extra lump sum"
        />

        <div style={pmtStyles.checkRow}>
          <input
            type="checkbox"
            id="createReceipt"
            checked={createReceipt}
            onChange={e => setCreateReceipt(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          <label htmlFor="createReceipt" style={pmtStyles.checkLabel}>
            Record as Mortgage/Rent budget expense
          </label>
        </div>
      </div>

      {/* Breakdown preview */}
      {total > 0 && (
        <div style={pmtStyles.breakdownCard}>
          <div style={pmtStyles.breakdownRow}>
            <span style={pmtStyles.breakdownLabel}>Interest</span>
            <span style={pmtStyles.breakdownValue}>${interestUsed.toFixed(2)}</span>
          </div>
          <div style={pmtStyles.breakdownRow}>
            <span style={pmtStyles.breakdownLabel}>Principal</span>
            <span style={pmtStyles.breakdownValue}>${principalPaid.toFixed(2)}</span>
          </div>
          {extraPrincipal > 0.01 && (
            <div style={pmtStyles.breakdownRow}>
              <span style={{ ...pmtStyles.breakdownLabel, color: '#10b981' }}>Extra Principal</span>
              <span style={{ ...pmtStyles.breakdownValue, color: '#10b981' }}>+${extraPrincipal.toFixed(2)}</span>
            </div>
          )}
          <div style={pmtStyles.divider} />
          <div style={pmtStyles.breakdownRow}>
            <span style={pmtStyles.breakdownLabel}>Balance After</span>
            <span style={{ ...pmtStyles.breakdownValue, fontWeight: 700 }}>
              ${balanceAfter.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      )}

      {error && <p style={pmtStyles.error}>{error}</p>}

      <button style={pmtStyles.saveBtn} onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Log Payment'}
      </button>
    </div>
  );
}

const pmtStyles = {
  formCard: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: '20px 20px',
    border: '1px solid #e2e8f0',
    marginBottom: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: '#475569',
    marginTop: 12,
    marginBottom: 4,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    fontSize: 15,
    backgroundColor: '#f8fafc',
    boxSizing: 'border-box',
    color: '#1e293b',
  },
  checkRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 12,
  },
  checkLabel: {
    fontSize: 14,
    color: '#475569',
    cursor: 'pointer',
  },
  breakdownCard: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: '16px 20px',
    border: '1px solid #e2e8f0',
    marginBottom: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  breakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  breakdownLabel: {
    fontSize: 14,
    color: '#64748b',
  },
  breakdownValue: {
    fontSize: 14,
    color: '#1e293b',
  },
  divider: {
    height: 1,
    backgroundColor: '#e2e8f0',
    margin: '4px 0',
  },
  error: {
    color: '#ef4444',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 8,
  },
  saveBtn: {
    width: '100%',
    padding: '14px 0',
    backgroundColor: '#6366f1',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
  },
};
